import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import Lenis from "lenis";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, ShieldCheck, Download, ArrowRight, Loader2, BadgeCheck, Timer, Star, Zap, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { initPixel, trackEvent } from "@/lib/pixel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BUNDLE = [
  { num: "01", fa: "fa-brands fa-wordpress", title: "500+ Premium WordPress Themes", desc: "Astra Pro, Divi, GeneratePress and more — ready-made premium themes for every niche, with lifetime updates." },
  { num: "02", fa: "fa-solid fa-plug", title: "Premium Pro Plugins", desc: "Elementor Pro, WP Rocket, Rank Math Pro — plugins that normally cost ₹50,000+, all inside this one bundle." },
  { num: "03", fa: "fa-solid fa-graduation-cap", title: "Dropshipping Mastery Course", desc: "From zero to store launch — a step-by-step course covering product research, ads and scaling." },
  { num: "04", fa: "fa-brands fa-shopify", title: "Premium Shopify Themes", desc: "High-converting Shopify themes that usually sell for $180+ each. Ready to upload, ready to sell." },
];

const FAQS = [
  { q: "How will I receive the bundle?", a: "Instantly. The moment your payment is confirmed, your Google Drive access link appears on the thank-you page. No waiting, no shipping — just click and download." },
  { q: "Is this a one-time payment?", a: "Yes. You pay once and get lifetime access to everything — all 500+ themes, pro plugins, the dropshipping course and Shopify themes, with lifetime free updates. No subscriptions, no hidden charges." },
  { q: "Are the themes and plugins original?", a: "Absolutely. All files are 100% original and GPL-licensed — the exact same files distributed by the original developers, untouched and safe to use." },
  { q: "Can I use these on client projects?", a: "Yes. Under the GPL license you can use the themes and plugins on unlimited personal and client websites. You just cannot resell the bundle itself." },
  { q: "What if the drive link doesn't work for me?", a: "Just email us at support@floxora.in with your Order ID and we'll fix it within 24–72 hours. If we can't resolve it, our Refund Policy covers you." },
  { q: "I'm a beginner. Will this work for me?", a: "That's exactly who this bundle is made for. The dropshipping course starts from zero, and the themes/plugins come ready to install — no coding needed." },
];

const REVIEWS = [
  { name: "Rahul Verma", role: "Dropshipper", text: "Bundle quality exceeded my expectations. My store was live in 2 days with a premium theme and the course made everything clear." },
  { name: "Priya Sharma", role: "Blogger", text: "The plugins alone are worth 10x the price. Elementor Pro and WP Rocket worked flawlessly on all my sites." },
  { name: "Amit Patel", role: "Agency Owner", text: "I use these themes for client projects daily. Instant drive access after payment — no waiting, no drama." },
  { name: "Sneha Gupta", role: "Shopify Seller", text: "The Shopify themes converted better than the $180 one I bought earlier. Best investment for my store this year." },
];

const DEFAULTS = {
  price: 499,
  compare_price: 9999,
  product_name: "Floxora Digital Mega Bundle",
  hero_line1: "Everything Premium.",
  hero_line2: "One Bundle.",
  hero_line3: "One Price.",
  hero_subtitle: "500+ premium WordPress themes, pro plugins, a complete dropshipping course and Shopify themes — all in one place. Get access today, launch today.",
  marquee_text: "500+ WORDPRESS THEMES • PRO PLUGINS • DROPSHIPPING COURSE • SHOPIFY THEMES",
  support_email: "support@floxora.in",
};

function Countdown() {
  const [t, setT] = useState({ h: "00", m: "00", s: "00" });
  useEffect(() => {
    const tick = () => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setT({
        h: String(Math.floor(diff / 3600)).padStart(2, "0"),
        m: String(Math.floor((diff % 3600) / 60)).padStart(2, "0"),
        s: String(diff % 60).padStart(2, "0"),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10 text-center" data-testid="countdown-timer">
      <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-5 flex items-center justify-center gap-2">
        <Timer className="w-4 h-4" /> Limited Offer Ends In
      </p>
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {[{ v: t.h, l: "Hours" }, { v: t.m, l: "Minutes" }, { v: t.s, l: "Seconds" }].map((u, i) => (
          <div key={u.l} className="flex items-center gap-2 sm:gap-3">
            <div className="bg-[#0b0b10] border border-[#A78BFA]/40 rounded-xl px-4 py-3 sm:px-6 sm:py-4 gold-glow min-w-[72px] sm:min-w-[96px]">
              <span className="font-['Outfit'] font-extrabold text-2xl sm:text-4xl text-white tabular-nums">{u.v}</span>
              <p className="text-zinc-500 text-[9px] sm:text-[10px] tracking-widest uppercase mt-1">{u.l}</p>
            </div>
            {i < 2 && <span className="font-['Outfit'] font-extrabold text-2xl sm:text-4xl text-[#A78BFA] animate-pulse">:</span>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [config, setConfig] = useState(DEFAULTS);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.3, 0.05]);

  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    let visitorId = localStorage.getItem("floxora_visitor_id");
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem("floxora_visitor_id", visitorId);
    }
    api.post("/track", { visitor_id: visitorId }).catch(() => {});
    api.get("/config").then((r) => {
      setConfig({ ...DEFAULTS, ...r.data });
      initPixel(r.data.meta_pixel_id);
      trackEvent("PageView");
    }).catch(() => {});
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handleBuy = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      trackEvent("Lead");
      const { data: order } = await api.post("/payments/create-order", form);
      if (order.mode === "demo") {
        await api.post("/payments/verify", { order_id: order.order_id });
        navigate(`/thankyou?order=${order.order_id}`);
        return;
      }
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("script");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Floxora",
        description: config.product_name,
        order_id: order.order_id,
        prefill: { name: form.name, contact: form.phone },
        theme: { color: "#A78BFA" },
        handler: async (resp) => {
          await api.post("/payments/verify", {
            order_id: order.order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          navigate(`/thankyou?order=${order.order_id}`);
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong, please try again");
    } finally {
      setLoading(false);
    }
  };

  const scrollToBuy = () => document.getElementById("buy")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-[#050505] text-white overflow-x-clip" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/logo-text.png" alt="Floxora" className="h-8 sm:h-10" />
          </Link>
          <button
            data-testid="nav-buy-button"
            onClick={scrollToBuy}
            className="rounded-full bg-gradient-to-r from-[#F472B6] via-[#A78BFA] to-[#60A5FA] text-white font-bold px-6 py-2.5 sm:px-7 text-sm hover:opacity-90 transition-opacity active:scale-95"
          >
            Get The Bundle
          </button>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-32 pb-20" data-testid="hero-section">
        <div className="absolute inset-0 -z-10 bg-grid opacity-70" />
        <motion.div style={{ opacity: bgOpacity }} className="absolute inset-0 -z-10">
          <div className="absolute left-[15%] top-16 w-[500px] h-[500px] rounded-full bg-[#F472B6]/12 blur-[130px]" />
          <div className="absolute left-1/2 top-32 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[#A78BFA]/15 blur-[130px]" />
          <div className="absolute right-[8%] bottom-[-80px] w-[450px] h-[450px] rounded-full bg-[#60A5FA]/12 blur-[120px]" />
        </motion.div>
        <div className="max-w-5xl mx-auto px-6 w-full text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#A78BFA]/50 bg-[#A78BFA]/10 px-4 py-2 sm:px-5 mb-8"
          >
            <img src="/logo-icon.png" alt="" className="h-4 w-4 shrink-0" />
            <span className="text-[#DDD6FE] font-medium tracking-widest uppercase text-[10px] sm:text-xs">India's Biggest Digital Asset Bundle</span>
          </motion.div>
          <h1 className="font-['Outfit'] font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">
            {[
              { text: config.hero_line1, blue: false },
              { text: `${config.hero_line2} ${config.hero_line3}`, blue: true },
            ].map((line, i) => (
              <span key={i} className={`block overflow-hidden pb-1 ${line.blue ? "text-gradient-gold" : ""}`}>
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.25 + i * 0.18, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="mt-7 max-w-2xl mx-auto text-sm sm:text-base md:text-lg text-zinc-400"
          >
            {config.hero_subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-5 sm:gap-6 justify-center"
          >
            <button
              data-testid="hero-buy-button"
              onClick={scrollToBuy}
              className="gold-glow rounded-full bg-gradient-to-r from-[#F472B6] via-[#A78BFA] to-[#60A5FA] text-white font-extrabold px-8 py-3.5 sm:px-10 sm:py-4 text-sm sm:text-base hover:opacity-90 transition-opacity active:scale-95 flex items-center gap-2"
            >
              Unlock The Bundle <ArrowRight className="w-5 h-5" />
            </button>
            <div className="flex items-baseline gap-3">
              <span className="text-zinc-500 line-through text-base sm:text-lg">₹{config.compare_price}</span>
              <span className="font-['Outfit'] font-extrabold text-2xl sm:text-3xl text-white" data-testid="hero-price">₹{config.price}</span>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            className="mt-14 flex items-center justify-center"
          >
            {[["500+", "Premium Themes"], ["₹1L+", "Total Value"], ["Lifetime", "Free Updates"]].map(([v, l]) => (
              <div key={l} className="text-center px-4 sm:px-10 border-l border-white/10 first:border-0">
                <p className="font-['Outfit'] font-extrabold text-xl sm:text-2xl text-gradient-gold">{v}</p>
                <p className="text-zinc-500 text-[9px] sm:text-xs tracking-widest uppercase mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-[#A78BFA]/25 py-5 -rotate-1 origin-left bg-black" data-testid="marquee-strip">
        <Marquee speed={40} gradient={false}>
          <span className="font-['Outfit'] font-extrabold text-3xl sm:text-6xl tracking-tighter text-stroke-gold mx-6 whitespace-nowrap">
            {config.marquee_text} • {config.marquee_text} •
          </span>
        </Marquee>
      </div>

      {/* Bento */}
      <section className="py-14 sm:py-20" data-testid="bundle-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            className="mb-10 text-center"
          >
            <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-3 flex items-center justify-center gap-2">
              <img src="/logo-icon.png" alt="" className="h-4 w-4" /> What's Inside
            </p>
            <h2 className="font-['Outfit'] font-extrabold tracking-tighter text-3xl sm:text-5xl lg:text-6xl text-white">
              Inside The <span className="text-gradient-gold">Bundle</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {BUNDLE.map((item, i) => (
              <motion.div
                key={item.num}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                whileHover={{ rotateX: 4, rotateY: -4 }}
                style={{ transformPerspective: 900 }}
                className="group relative bg-[#0b0b10] border border-white/10 rounded-2xl p-8 sm:p-10 hover:border-[#A78BFA]/60 hover:shadow-[0_0_50px_rgba(167,139,250,0.12)] transition-[border-color,box-shadow] duration-300"
                data-testid={`bundle-card-${item.num}`}
              >
                <div className="relative h-36 rounded-xl bg-gradient-to-br from-[#F472B6]/20 via-[#A78BFA]/20 to-[#60A5FA]/20 border border-white/10 flex items-center justify-center mb-7 overflow-hidden">
                  <div className="absolute inset-0 bg-grid opacity-50" />
                  <i className={`${item.fa} relative text-5xl text-[#DDD6FE] group-hover:scale-110 transition-transform duration-300`}></i>
                  <span className="absolute right-4 top-2 font-['Outfit'] font-extrabold text-5xl text-white/10">{item.num}</span>
                </div>
                <div>
                  <h3 className="font-['Outfit'] font-bold text-xl sm:text-2xl mb-3 text-white">{item.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-14 sm:py-20 border-t border-white/10 bg-black" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center lg:text-left">
            <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-3 flex items-center justify-center lg:justify-start gap-2">
              <img src="/logo-icon.png" alt="" className="h-4 w-4" /> Why Floxora
            </p>
            <h2 className="font-['Outfit'] font-extrabold tracking-tighter text-3xl sm:text-5xl leading-tight text-white">
              Bought separately, this stack costs over <span className="text-gradient-gold">₹1,00,000</span>.
            </h2>
          </motion.div>
          <div className="space-y-10">
            {[
              { n: "01", t: "Not Just Tools — A Business", d: "Themes + plugins + course + Shopify themes — a complete launch kit. Just bring your idea, everything else is ready." },
              { n: "02", t: "Instant Delivery, Zero Wait", d: "The moment your payment completes, your Google Drive access appears instantly. No shipping, no waiting, no hassle." },
              { n: "03", t: "Pay Once, Use Forever", d: "Lifetime access with lifetime updates. No monthly subscriptions, no hidden charges, ever." },
            ].map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="flex gap-6 text-left"
              >
                <span className="font-['Outfit'] font-extrabold text-[#A78BFA] text-lg">{c.n}</span>
                <div>
                  <h3 className="font-['Outfit'] font-bold text-lg sm:text-xl mb-2 text-white">{c.t}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{c.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee 2 */}
      <div className="border-y border-[#A78BFA]/25 py-5 rotate-1 origin-right bg-black" data-testid="marquee-strip-2">
        <Marquee speed={40} gradient={false} direction="right">
          <span className="font-['Outfit'] font-extrabold text-3xl sm:text-6xl tracking-tighter text-stroke-gold mx-6 whitespace-nowrap">
            LIMITED OFFER • LIFETIME ACCESS • INSTANT DELIVERY • LIMITED OFFER • LIFETIME ACCESS • INSTANT DELIVERY •
          </span>
        </Marquee>
      </div>

      {/* Spotlight */}
      <section className="py-14 sm:py-20 border-t border-white/10 relative overflow-hidden" data-testid="spotlight-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-[#A78BFA]/10 blur-[140px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-10 text-center"
          >
            <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-3 flex items-center justify-center gap-2">
              <img src="/logo-icon.png" alt="" className="h-4 w-4" /> The Vault
            </p>
            <h2 className="font-['Outfit'] font-extrabold tracking-tighter text-3xl sm:text-5xl lg:text-6xl text-white">
              One Drive. <span className="text-gradient-gold">Everything Inside.</span>
            </h2>
          </motion.div>
          <div className="grid lg:grid-cols-[1fr_1.7fr_1fr] gap-5 items-stretch">
            {/* Left mini card */}
            <motion.div
              initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="bg-[#0b0b10] border border-white/10 rounded-2xl p-8 flex flex-col justify-between hover:border-[#A78BFA]/50 transition-colors order-2 lg:order-1"
              data-testid="spotlight-card-instant"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F472B6]/20 to-[#60A5FA]/20 border border-[#A78BFA]/40 flex items-center justify-center">
                <Zap className="w-6 h-6 text-[#C4B5FD]" />
              </div>
              <div className="mt-12">
                <h3 className="font-['Outfit'] font-bold text-xl text-white mb-2">Instant Access</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">The moment your payment is confirmed, your drive link appears instantly on screen. No waiting, no delay.</p>
              </div>
            </motion.div>
            {/* Center showcase card */}
            <motion.div
              initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="relative bg-gradient-to-b from-[#14101f] to-[#0b0b10] border border-[#A78BFA]/30 rounded-3xl p-8 sm:p-12 gold-glow order-1 lg:order-2 overflow-hidden"
              data-testid="spotlight-card-main"
            >
              <div className="absolute inset-0 bg-grid opacity-40" />
              <div className="relative">
                <p className="text-[#DDD6FE] text-xs tracking-widest uppercase text-center mb-8">The Complete Mega Bundle</p>
                <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-md mx-auto mb-10">
                  {[
                    { fa: "fa-brands fa-wordpress", l: "Themes" },
                    { fa: "fa-solid fa-plug", l: "Plugins" },
                    { fa: "fa-solid fa-graduation-cap", l: "Course" },
                    { fa: "fa-brands fa-shopify", l: "Shopify" },
                  ].map((t) => (
                    <div key={t.l} className="flex flex-col items-center gap-2.5">
                      <div className="w-full aspect-square rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center hover:border-[#A78BFA]/60 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                        <i className={`${t.fa} text-2xl sm:text-3xl text-[#C4B5FD]`}></i>
                      </div>
                      <span className="text-zinc-500 text-[10px] tracking-widest uppercase">{t.l}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                  <div className="flex items-baseline gap-2.5">
                    <span className="text-zinc-500 line-through">₹{config.compare_price}</span>
                    <span className="font-['Outfit'] font-extrabold text-3xl text-gradient-gold">₹{config.price}</span>
                  </div>
                  <button
                    onClick={scrollToBuy}
                    data-testid="spotlight-buy-button"
                    className="rounded-full bg-gradient-to-r from-[#F472B6] via-[#A78BFA] to-[#60A5FA] text-white font-bold px-8 py-3 text-sm hover:opacity-90 transition-opacity active:scale-95 flex items-center gap-2"
                  >
                    Unlock Everything <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
            {/* Right mini card */}
            <motion.div
              initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="bg-[#0b0b10] border border-white/10 rounded-2xl p-8 flex flex-col justify-between hover:border-[#A78BFA]/50 transition-colors order-3"
              data-testid="spotlight-card-updates"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F472B6]/20 to-[#60A5FA]/20 border border-[#A78BFA]/40 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-[#C4B5FD]" />
              </div>
              <div className="mt-12">
                <h3 className="font-['Outfit'] font-bold text-xl text-white mb-2">Lifetime Updates</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">New themes and plugin updates land in the same drive link forever. Pay once, stay updated.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee 3 */}
      <div className="border-y border-[#A78BFA]/25 py-5 -rotate-1 origin-left bg-black" data-testid="marquee-strip-3">
        <Marquee speed={40} gradient={false}>
          <span className="font-['Outfit'] font-extrabold text-3xl sm:text-6xl tracking-tighter text-stroke-gold mx-6 whitespace-nowrap">
            SECRET VAULT • QUANTITY LIMITED • ONE-TIME PAYMENT • SECRET VAULT • QUANTITY LIMITED • ONE-TIME PAYMENT •
          </span>
        </Marquee>
      </div>

      {/* Testimonials */}
      <section className="py-14 sm:py-20 border-t border-white/10" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-10 text-center"
          >
            <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-3 flex items-center justify-center gap-2">
              <img src="/logo-icon.png" alt="" className="h-4 w-4" /> Buyer Reviews
            </p>
            <h2 className="font-['Outfit'] font-extrabold tracking-tighter text-3xl sm:text-5xl lg:text-6xl text-white">
              What Our <span className="text-gradient-gold">Buyers Say</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={r.name}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                whileHover={{ rotateX: 4, rotateY: -4 }}
                style={{ transformPerspective: 900 }}
                className="bg-[#0b0b10] border border-white/10 rounded-2xl p-7 hover:border-[#A78BFA]/50 hover:shadow-[0_0_50px_rgba(167,139,250,0.12)] transition-[border-color,box-shadow] duration-300 flex flex-col"
                data-testid={`review-card-${i}`}
              >
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-[#F472B6] text-[#F472B6]" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed mb-7 flex-1">"{r.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F472B6] via-[#A78BFA] to-[#60A5FA] flex items-center justify-center text-white font-['Outfit'] font-extrabold text-sm shrink-0">
                    {r.name[0]}
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{r.name}</p>
                    <p className="text-zinc-500 text-xs">{r.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing / Buy */}
      <section id="buy" className="py-14 sm:py-20 relative" data-testid="pricing-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-[10%] top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#F472B6]/10 blur-[130px]" />
          <div className="absolute right-[10%] top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#60A5FA]/10 blur-[130px]" />
        </div>
        <div className="max-w-5xl mx-auto px-6">
          <Countdown />
          <motion.div
            initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="grid lg:grid-cols-2 rounded-3xl overflow-hidden border border-[#A78BFA]/30 gold-glow bg-[#0b0b10]"
          >
            {/* Left: offer summary */}
            <div className="p-8 sm:p-12 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col justify-between text-center lg:text-left">
              <div>
                <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-4">Limited Time Offer</p>
                <span
                  className="inline-block rounded-full bg-[#A78BFA]/15 border border-[#A78BFA]/40 text-[#DDD6FE] text-xs font-bold px-4 py-1.5 mb-5"
                  data-testid="save-badge"
                >
                  Save {Math.max(0, Math.round((1 - config.price / config.compare_price) * 100))}% Today
                </span>
                <div className="flex items-baseline justify-center lg:justify-start gap-4">
                  <span className="text-zinc-500 line-through text-2xl">₹{config.compare_price}</span>
                  <span className="font-['Outfit'] font-extrabold text-5xl sm:text-7xl tracking-tighter text-gradient-gold" data-testid="bundle-price">₹{config.price}</span>
                </div>
                <p className="text-zinc-400 text-sm mt-3">{config.product_name} — one-time payment, lifetime access</p>
              </div>
              <ul className="mt-10 space-y-4 text-left max-w-sm mx-auto lg:mx-0">
                {[
                  "500+ premium WordPress themes",
                  "All premium pro plugins included",
                  "Complete dropshipping course",
                  "Premium Shopify themes",
                  "Instant Google Drive delivery",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                    <BadgeCheck className="w-5 h-5 text-[#A78BFA] shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            {/* Right: form */}
            <div className="p-8 sm:p-12 bg-[#0d0d14]">
              <h3 className="font-['Outfit'] font-bold text-xl mb-1 text-center lg:text-left text-white">Get Instant Access</h3>
              <p className="text-zinc-500 text-xs mb-8 text-center lg:text-left">No email required — your access link appears right after payment.</p>
              <form onSubmit={handleBuy} className="space-y-5" data-testid="buy-form">
                <div className="space-y-2">
                  <Label htmlFor="buyer-name" className="text-zinc-300">Your Name</Label>
                  <Input
                    id="buyer-name" data-testid="buyer-name-input" placeholder="Enter your name"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-black/40 border-white/15 text-white h-12 rounded-xl focus-visible:ring-[#A78BFA]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buyer-phone" className="text-zinc-300">Phone Number</Label>
                  <Input
                    id="buyer-phone" data-testid="buyer-phone-input" placeholder="Enter your number"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-black/40 border-white/15 text-white h-12 rounded-xl focus-visible:ring-[#A78BFA]"
                  />
                </div>
                <button
                  type="submit" disabled={loading} data-testid="buy-now-button"
                  className="btn-shine gold-glow w-full rounded-full bg-gradient-to-r from-[#F472B6] via-[#A78BFA] to-[#60A5FA] text-white font-extrabold py-4 text-base sm:text-lg hover:opacity-90 transition-opacity active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
                  {loading ? "Processing..." : `Pay ₹${config.price} — Get Instant Access`}
                </button>
              </form>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#A78BFA]" /> Secure Razorpay Payment</span>
                <span className="flex items-center gap-1.5"><Download className="w-4 h-4 text-[#A78BFA]" /> Instant Drive Link</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 border-t border-white/10" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mb-14 text-center"
          >
            <p className="text-[#DDD6FE] text-xs tracking-widest uppercase mb-3 flex items-center justify-center gap-2">
              <img src="/logo-icon.png" alt="" className="h-4 w-4" /> FAQ
            </p>
            <h2 className="font-['Outfit'] font-extrabold tracking-tighter text-3xl sm:text-5xl lg:text-6xl text-white">
              Questions? <span className="text-gradient-gold">Answers.</span>
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-[#0b0b10] border border-white/10 rounded-2xl px-6 data-[state=open]:border-[#A78BFA]/50 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-['Outfit'] font-bold text-white text-base sm:text-lg hover:no-underline py-5" data-testid={`faq-question-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 text-sm leading-relaxed pb-5" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 bg-black" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
            <Link to="/" className="flex items-center" data-testid="footer-brand-logo">
              <img src="/logo-text.png" alt="Floxora" className="h-10" />
            </Link>
            <a href={`mailto:${config.support_email}`} className="text-zinc-400 text-sm hover:text-[#C4B5FD] transition-colors" data-testid="support-email-link">
              {config.support_email}
            </a>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/5 pt-6">
            <p className="text-zinc-500 text-xs">© 2026 Floxora. All rights reserved.</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-zinc-500">
              <Link to="/legal/privacy-policy" className="hover:text-[#C4B5FD] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
              <Link to="/legal/terms" className="hover:text-[#C4B5FD] transition-colors" data-testid="footer-terms-link">Terms & Conditions</Link>
              <Link to="/legal/disclaimer" className="hover:text-[#C4B5FD] transition-colors" data-testid="footer-disclaimer-link">Disclaimer</Link>
              <Link to="/legal/refund-policy" className="hover:text-[#C4B5FD] transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
